import { createContext, useContext, useState, type ReactNode } from "react";

export type Lang = "en" | "zh";

function detectLang(): Lang {
  const saved = localStorage.getItem("lang");
  if (saved === "en" || saved === "zh") return saved;
  const nav = navigator.language || "";
  return nav.startsWith("zh") ? "zh" : "en";
}

const LangContext = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({
  lang: "en",
  setLang: () => {},
});

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);
  const setLang = (l: Lang) => {
    localStorage.setItem("lang", l);
    setLangState(l);
  };
  return (
    <LangContext.Provider value={{ lang, setLang }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}

export function useT() {
  const { lang } = useLang();
  return (key: keyof typeof translations) => translations[key][lang];
}

const translations = {
  // common
  backHome: { en: "Back to Home", zh: "返回首页" },
  privacy: { en: "Privacy Policy", zh: "隐私协议" },
  terms: { en: "Terms of Use", zh: "使用条款" },
  lastUpdated: { en: "Last updated: March 19, 2026", zh: "最后更新日期：2026 年 3 月 19 日" },
  copyright: { en: "\u00a9 2026 Joker Gambit. All rights reserved.", zh: "\u00a9 2026 Joker Gambit. All rights reserved." },
  contactUs: { en: "Contact Us", zh: "联系我们" },

  // home
  homeSubtitle: { en: "Roguelike Elemental Card Game", zh: "Roguelike 元素卡牌策略游戏" },
  homeDesc: {
    en: "Build your grimoire, collect 120+ powerful artifacts,\nand conquer every floor. A new strategy every run.",
    zh: "构建你的魔法书，收集 120+ 强力神器，突破每一层挑战。\n每一局都是全新策略体验。",
  },

  // privacy page
  privacyOverviewTitle: { en: "Overview", zh: "概述" },
  privacyOverview: {
    en: 'Joker Gambit (hereinafter "the App") respects and protects your personal privacy. This Privacy Policy explains how we collect, use, and protect your information.',
    zh: "Joker Gambit（以下简称\u201c本应用\u201d）尊重并保护您的个人隐私。本隐私协议旨在说明我们如何收集、使用和保护您的信息。",
  },
  privacyCollectionTitle: { en: "Information Collection", zh: "信息收集" },
  privacyCollection: {
    en: "The App does not actively collect your personally identifiable information. We may collect the following anonymous data to improve user experience:",
    zh: "本应用不会主动收集您的个人身份信息。我们可能会收集以下匿名数据以改善用户体验：",
  },
  privacyCollectionItem1: { en: "Device model and OS version", zh: "设备型号与操作系统版本" },
  privacyCollectionItem2: { en: "App crash logs", zh: "应用崩溃日志" },
  privacyCollectionItem3: { en: "Basic usage statistics", zh: "基本使用统计数据" },
  privacyUsageTitle: { en: "Data Usage", zh: "数据使用" },
  privacyUsage: {
    en: "The anonymous data collected is used solely for the following purposes:",
    zh: "收集的匿名数据仅用于以下目的：",
  },
  privacyUsageItem1: { en: "Improving app performance and stability", zh: "改善应用性能和稳定性" },
  privacyUsageItem2: { en: "Fixing known issues", zh: "修复已知问题" },
  privacyUsageItem3: { en: "Optimizing user experience", zh: "优化用户体验" },
  privacyStorageTitle: { en: "Data Storage", zh: "数据存储" },
  privacyStorage: {
    en: "Your game progress and settings are stored only on your local device. We do not upload your personal data to any server.",
    zh: "您的游戏进度和设置数据仅存储在您的本地设备上。我们不会将您的个人数据上传至任何服务器。",
  },
  privacyThirdPartyTitle: { en: "Third-Party Services", zh: "第三方服务" },
  privacyThirdParty: {
    en: "The App may use analytics services provided by Apple and RevenueCat for subscription management. The handling of related data is subject to their respective privacy policies.",
    zh: "本应用可能使用 Apple 提供的分析服务以及 RevenueCat 进行订阅管理。相关数据的处理遵循其各自的隐私政策。",
  },
  privacyChildrenTitle: { en: "Children's Privacy", zh: "儿童隐私" },
  privacyChildren: {
    en: "The App does not knowingly collect personal information from children under 13. If you discover that we have inadvertently collected a child's information, please contact us and we will delete it immediately.",
    zh: "本应用不会故意收集 13 岁以下儿童的个人信息。如果您发现我们无意中收集了儿童的信息，请联系我们，我们将立即删除。",
  },
  privacyChangesTitle: { en: "Policy Changes", zh: "协议变更" },
  privacyChanges: {
    en: "We may update this Privacy Policy from time to time. Updated policies will be posted on this page. We recommend you review it periodically.",
    zh: "我们可能会不定期更新本隐私协议。更新后的协议将在本页面发布，建议您定期查看。",
  },
  privacyContact: {
    en: "If you have any questions about this Privacy Policy, please contact us via in-app feedback or email at\u00a0",
    zh: "如果您对本隐私协议有任何疑问，请通过应用内的反馈功能或发送邮件至\u00a0",
  },

  // terms page
  termsIntroTitle: { en: "About", zh: "关于" },
  termsIntro: {
    en: "Joker Gambit is a single-player roguelike card game. By using this app, you agree to the following simple terms.",
    zh: "Joker Gambit 是一款单机 Roguelike 卡牌游戏。使用本应用即表示您同意以下条款。",
  },
  termsGameDataTitle: { en: "Game Data", zh: "游戏数据" },
  termsGameData: {
    en: "All game progress is stored on your device and synced via iCloud. Uninstalling the app may delete local data, but iCloud backups can be restored.",
    zh: "所有游戏进度保存在您的本地设备上，并通过 iCloud 同步。卸载应用可能会删除本地数据，但 iCloud 备份可以恢复。",
  },
  termsVirtualTitle: { en: "Virtual Items", zh: "虚拟物品" },
  termsVirtual: {
    en: "All in-game items (jokers, currency, etc.) are for entertainment only and have no real-world value.",
    zh: "游戏内的所有物品（小丑、金币等）仅供娱乐，没有现实价值。",
  },
  termsSubscriptionTitle: { en: "Subscriptions & Purchases", zh: "订阅与购买" },
  termsSubscriptionIntro: {
    en: "Joker Gambit offers the following optional in-app purchases to unlock the full game experience:",
    zh: "Joker Gambit 提供以下可选的应用内购买以解锁完整游戏体验：",
  },
  termsSubscriptionMonthly: {
    en: "Monthly Subscription: $0.99/month, billed automatically through your Apple ID.",
    zh: "月度订阅：$0.99/月，通过您的 Apple ID 自动扣费。",
  },
  termsSubscriptionLifetime: {
    en: "Lifetime Unlock: $4.99 one-time purchase, permanently unlocks all features.",
    zh: "永久解锁：$4.99 一次性购买，永久解锁所有功能。",
  },
  termsSubscriptionAutoRenew: {
    en: "The monthly subscription automatically renews unless canceled at least 24 hours before the end of the current billing period. Your Apple ID account will be charged for renewal within 24 hours prior to the end of the current period.",
    zh: "月度订阅会自动续费，除非在当前计费周期结束前至少 24 小时取消。您的 Apple ID 账户将在当前周期结束前 24 小时内被扣费续订。",
  },
  termsSubscriptionManage: {
    en: "You can manage or cancel your subscription at any time in your device's Settings → Apple ID → Subscriptions.",
    zh: "您可以随时在设备的\u201c设置 → Apple ID → 订阅\u201d中管理或取消订阅。",
  },
  termsSubscriptionRestore: {
    en: "You can restore previous purchases at any time through the app. Subscriptions and lifetime purchases are tied to your Apple ID.",
    zh: "您可以随时通过应用恢复之前的购买。订阅和永久购买与您的 Apple ID 绑定。",
  },
  termsDisclaimerTitle: { en: "Disclaimer", zh: "免责声明" },
  termsDisclaimer: {
    en: "The app is provided as-is. We do our best to keep it running smoothly but cannot guarantee it will be error-free at all times.",
    zh: "本应用按现状提供。我们会尽力保持稳定运行，但无法保证完全没有错误。",
  },
  termsContact: {
    en: "If you have any questions, feel free to reach us at\u00a0",
    zh: "如有任何问题，欢迎联系我们：",
  },
} as const;
